import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { accountHeaders } from "@/lib/account";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const toKey = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const loadSubscription = () =>
  navigator.serviceWorker.getRegistration("/sw.js").then((reg) => reg?.pushManager.getSubscription()).then((s) => s || null);

export const DailyPushToggle = ({ label = "Avvisami ogni mattina", hideWhenActive = false, compact = false }) => {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (supported()) loadSubscription().then(setSub);
  }, []);

  if (!supported()) return null;

  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") {
        toast.error("Notifiche bloccate: abilitale nelle impostazioni del browser.");
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/key`);
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) });
      await axios.post(`${API}/push/subscribe`, s.toJSON(), { headers: accountHeaders() });
      setSub(s);
      toast.success("Fatto! Riceverai l'idea del giorno ogni mattina e gli avvisi sulle tue idee condivise.");
    } catch {
      toast.error("Impossibile attivare le notifiche su questo browser.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
    setSub(null);
    setBusy(false);
    toast.success("Notifiche disattivate");
  };

  if (sub && hideWhenActive) return <p className="text-xs text-emerald-300" data-testid="daily-push-active-note">Avvisi attivi su questo browser.</p>;
  const size = compact ? "px-4 py-2 text-xs" : "px-5 py-3 text-sm";

  return sub ? (
    <button onClick={disable} disabled={busy} data-testid="daily-push-disable-btn"
      className={`inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 ${size} font-semibold text-emerald-300 transition-colors hover:border-rose-400/50 hover:text-rose-300`}>
      <BellOff className="w-4 h-4" /> Avvisi attivi · disattiva
    </button>
  ) : (
    <button onClick={enable} disabled={busy} data-testid="daily-push-enable-btn"
      className={`inline-flex items-center gap-2 rounded-full border border-white/15 ${size} font-semibold text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-300 disabled:opacity-50`}>
      <Bell className="w-4 h-4" /> {busy ? "Attivazione…" : label}
    </button>
  );
};
